import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { toast } from "sonner";
import { ChevronLeft, ArrowUp, Trash2, ShieldCheck, Lock } from "lucide-react";

export default function SharedChat() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [chat, setChat] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [notMember, setNotMember] = useState(false);
  const scrollRef = useRef(null);
  usePageMeta(t("shared_coaching_chat"));

  const load = useCallback(async () => {
    if (!chatId) return;
    const [all, m] = await Promise.all([
      api.get("/collaboration/shared-chat").then((r) => r.data).catch(() => []),
      api.get(`/collaboration/shared-chat/${chatId}/messages`).then((r) => r.data).catch((e) => {
        if (e?.response?.status === 403) setNotMember(true);
        return [];
      }),
    ]);
    setChat((Array.isArray(all) ? all : []).find((c) => c.id === chatId) || null);
    setMsgs(Array.isArray(m) ? m : []);
  }, [chatId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setText("");
    try { await api.post(`/collaboration/shared-chat/${chatId}/messages`, { body }); await load(); }
    catch (e) { toast.error(apiError(e)); setText(body); }
    finally { setSending(false); }
  };

  const remove = async (m) => {
    if (m.deleted) return;
    setMsgs((prev) => prev.map((x) => (x.id === m.id ? { ...x, deleted: true, body: "" } : x)));
    try { await api.delete(`/collaboration/shared-chat/${chatId}/messages/${m.id}`); }
    catch (e) { toast.error(apiError(e)); await load(); }
  };

  const memberNames = chat ? [chat.coach_a_name, chat.coach_b_name, chat.client_name].filter(Boolean).join(" · ") : "";

  return (
    <div className="mx-auto flex h-[calc(100vh-11rem)] max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-card" data-testid="shared-chat-screen">
      <div className="flex items-center gap-3 border-b border-border p-3">
        <button onClick={() => navigate("/app/messages")} data-testid="shared-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-base text-foreground">{t("shared_coaching_chat")}</p>
          {memberNames ? <p className="truncate text-xs text-muted-foreground">{memberNames}</p> : null}
        </div>
      </div>

      {notMember ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center" data-testid="shared-chat-not-member">
          <Lock className="h-7 w-7 text-muted-foreground" />
          <p className="font-display text-lg text-foreground">{t("shared_chat_no_access_title")}</p>
          <p className="max-w-sm text-sm text-muted-foreground">{t("shared_chat_no_access_body")}</p>
          <button onClick={() => navigate("/app/messages")} data-testid="shared-back-cta" className="mt-1 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">{t("back")}</button>
        </div>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="shared-messages">
            <div className="mb-2 flex items-center gap-2 rounded-xl bg-primary/10 px-3 py-2 text-xs text-foreground">
              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" /> {t("shared_chat_banner")}
            </div>
            {msgs === null ? <Spinner label={t("common.loading")} /> : msgs.length === 0 ? (
              <p className="py-10 text-center text-sm italic text-muted-foreground">{t("shared_chat_empty")}</p>
            ) : msgs.map((m) => {
              const mine = m.from_user_id === user?.id;
              return (
                <div key={m.id} className={`group flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`shared-msg-${m.id}`}>
                  {!mine ? <span className={`mb-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${m.from_role === "client" ? "bg-sky-100 text-sky-700" : "bg-primary/15 text-primary"}`}>{(m.from_name || "").split(" ")[0] || m.from_role}</span> : null}
                  <div className="flex items-center gap-2">
                    {mine && !m.deleted ? <button onClick={() => remove(m)} data-testid={`shared-delete-${m.id}`} className="opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-rose-600" /></button> : null}
                    <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${m.deleted ? "bg-secondary/50 italic text-muted-foreground" : mine ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-foreground"}`}>
                      {m.deleted ? t("message_deleted") : m.body}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-end gap-2 border-t border-border p-3">
            <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={t("shared_chat_placeholder")} rows={1} data-testid="shared-input" className="max-h-28 min-h-[44px] flex-1 resize-none rounded-2xl border border-border bg-background px-3.5 py-2.5 text-sm outline-none focus:border-primary" />
            <button onClick={send} disabled={!text.trim() || sending} data-testid="shared-send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"><ArrowUp className="h-5 w-5" /></button>
          </div>
        </>
      )}
    </div>
  );
}
